<template>
  <div class="flex items-center gap-6 relative cursor-pointer" data-e2e="file-upload">
    <input
        type="file"
        :accept="acceptAttr"
        class="absolute opacity-0 inset-0 cursor-pointer"
        ref="fileInput"
        @change="handleFileUpload"
        data-e2e="file-upload-input"
    />

    <!-- Image upload area -->
    <div
        class="relative size-20 bg-gray-800 border border-dashed border-border-segment rounded-full flex items-center justify-center flex-shrink-0"
        @click="triggerFileInput"
        @dragover.prevent
        @drop="handleDrop"
    >
      <div v-if="loading" class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <b-loader-spinner class="text-gray-300" />
      </div>
      <div v-else>
        <!-- Preview image or upload icon -->
        <div v-if="previewImage || props.url" class="size-20 rounded-full overflow-hidden" data-e2e="file-upload-preview">
          <img :src="previewImage || props.url" :alt="$t('bFileUpload.profilePreview')" class="w-full h-full object-cover">
        </div>
        <div v-else class="flex items-center justify-center">
          <Icon name="hugeicons:image-add-02" class="size-6 text-text-secondary" />
        </div>
      </div>
    </div>

    <!-- Upload instructions -->
    <div class="flex flex-col gap-1.5">
      <p class="text-base font-medium text-gray-100">{{ props.title ?? $t('bFileUpload.selectProfilePicture') }}</p>
      <p class="text-sm font-medium text-text-secondary">{{ props.subtitle ?? $t('bFileUpload.acceptedFormats') }}</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import BLoaderSpinner from "~/components/loader/BLoaderSpinner.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { trackEvent } = useTracking();
const model = defineModel()

const DEFAULT_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

interface Props {
  loading: boolean;
  url: string;
  title?: string;
  subtitle?: string;
  /** MIME types allowed for selection/validation. Defaults to PNG/JPG. */
  acceptedTypes?: string[];
  /** Maximum file size in MB. When set, larger files are rejected client-side. */
  maxSizeMb?: number;
}

const props = defineProps<Props>()

const allowedTypes = computed(() => props.acceptedTypes ?? DEFAULT_TYPES);
const acceptAttr = computed(() => allowedTypes.value.join(', '));

const fileInput = ref(null)
const previewImage = ref(null)

const emits = defineEmits(["update:modelValue", "change"]);

// Trigger file input click
const triggerFileInput = () => {
  fileInput.value.click()
}

// Handle file upload (input change)
const handleFileUpload = (event) => {
  const file = event.target.files[0]
  processFile(file)
}

// Handle drag & drop upload
const handleDrop = (event) => {
  event.preventDefault()
  const file = event.dataTransfer.files[0]
  processFile(file)
}

// Process file (validate and set preview)
const processFile = (file) => {
  if (!file) return
  if (!allowedTypes.value.includes(file.type)) {
    alert(t('bFileUpload.onlyPngAllowed'))
    return
  }
  if (props.maxSizeMb && file.size > props.maxSizeMb * 1024 * 1024) {
    alert(t('bFileUpload.fileTooLarge', { size: props.maxSizeMb }))
    return
  }
  const reader = new FileReader()
  reader.onload = (e) => {
    previewImage.value = e.target.result
    model.value = file; // Update the model with the file
    emits("update:modelValue", file);
    emits("change", file);
    trackEvent("document", "document_selected");
  }
  reader.readAsDataURL(file)
}
</script>
